import { useState } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LessonManager from './LessonManager';
import { createSectionApi } from '../../../../api/apiServices';

export default function SectionManager({ courseId, sections = [], onChange }) {
  const [expandedSectionId, setExpandedSectionId] = useState(null); // Use a dedicated state for expanded
  const [creatingSection, setCreatingSection] = useState(false);

  const handleAddSection = async () => {
    if (!courseId) return;

    setCreatingSection(true);

    try {
      const defaultTitle = `Section ${sections.length + 1}`;
      const response = await createSectionApi(courseId, defaultTitle);

      if (response.success && response.section) {
        const backendSection = response.section;

        const newSection = {
          _id: backendSection._id,                    // Store real MongoDB _id
          id: backendSection._id,                     // Keep both for compatibility
          title: backendSection.title || defaultTitle,
          order: sections.length,
          lessons: backendSection.lectures || []      // Use lectures from backend
        };

        onChange([...sections, newSection]);
      }
    } catch (err) {
      console.error("Failed to create section:", err);
      alert(err.response?.data?.message || "Failed to create section");
    } finally {
      setCreatingSection(false);
    }
  };

  const onDragEnd = (result) => {
    const { destination, source, type } = result;
    if (!destination) return;

    if (type === 'SECTION') {
      const newSections = Array.from(sections);
      const [moved] = newSections.splice(source.index, 1);
      newSections.splice(destination.index, 0, moved);

      const updated = newSections.map((s, i) => ({ ...s, order: i }));
      onChange(updated);
      return;
    }
  };

  const updateSectionLessons = (sectionId, newLessons) => {
    const updatedSections = sections.map(section => {
      const currentId = section._id || section.id;
      return currentId === sectionId 
        ? { ...section, lessons: newLessons } 
        : section;
    });
    onChange(updatedSections);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <div className="max-w-5xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Course Content</h2>
            <p className="text-slate-500 mt-1">{sections.length} sections</p>
          </div>

          <button
            onClick={handleAddSection}
            disabled={creatingSection}
            className="flex items-center gap-2 px-6 py-3 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold rounded-2xl transition-colors"
          >
            {creatingSection ? (
              <><i className="ri-loader-4-line animate-spin" /> Creating...</>
            ) : (
              <><i className="ri-add-line" /> Add Section</>
            )}
          </button>
        </div>

        <Droppable droppableId="sections" type="SECTION">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-8">
              {sections.map((section, index) => {
                const sectionKey = section._id || section.id;   // Use real _id
                return (
                  <div key={sectionKey} className="bg-white border border-slate-200 rounded-3xl overflow-hidden">
                    {/* Section Header */}
                    <div className="px-6 py-5 bg-slate-50 border-b flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-8 h-8 bg-teal-100 text-teal-700 rounded-2xl flex items-center justify-center font-semibold">
                          {index + 1}
                        </div>
                        <h3 className="font-semibold text-lg text-slate-900">{section.title}</h3>
                      </div>

                      <button
                        onClick={() => setExpandedSectionId(expandedSectionId === sectionKey ? null : sectionKey)}
                        className="text-sm text-slate-500 hover:text-slate-700 font-medium"
                      >
                        {expandedSectionId === sectionKey ? 'Hide Lessons' : 'Show Lessons'}
                      </button>
                    </div>

                    {/* Lessons inside this section */}
                    {expandedSectionId === sectionKey && (
                      <div className="p-6">
                        <LessonManager
                          courseId={courseId}
                          sectionId={sectionKey}                    // ← Pass real _id
                          lessons={section.lectures || []}
                          onChange={(newLessons) => updateSectionLessons(sectionKey, newLessons)}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>

        {sections.length === 0 && (
          <div className="bg-white border border-dashed border-slate-300 rounded-3xl p-20 text-center">
            <p className="text-slate-500">No sections yet. Click "Add Section" to start building your course.</p>
          </div>
        )}
      </div>
    </DragDropContext>
  );
}